import { Dialog, Flex, Grid, IconButton, Portal, Spinner, Text } from '@chakra-ui/react'
import { LuX } from 'react-icons/lu'
import { useEventDetails } from '@/hooks/events/useEventDetails'
import { AGENDA_PERMISSIONS, hasPermission } from '@/utils/agendaPermissions'
import type { AgendaPermission } from '@/services/agendas'
import CustomDialogBackdrop from '@/components/CustomDialogBackdrop'
import GeneralSection from './GeneralSection'
import AddressSection from './AddressSection'
import RemindersSection from './RemindersSection'
import FinancialSection from './FinancialSection'
import DeleteEventButton from './DeleteEventButton'

type Props = {
  agendaId: string
  eventId: string
  permissions: AgendaPermission[]
  open: boolean
  onClose: () => void
}

const EventDetailsModal = ({ agendaId, eventId, permissions, open, onClose }: Props) => {
  const { data: event, isLoading } = useEventDetails(agendaId, eventId)

  const canEditEvents = hasPermission(permissions, AGENDA_PERMISSIONS.EDIT_EVENTS)
  const canEditNotes = hasPermission(permissions, AGENDA_PERMISSIONS.EDIT_NOTES)
  const canViewFinancial = hasPermission(permissions, AGENDA_PERMISSIONS.VIEW_FINANCIAL)
  const canEditFinancial = hasPermission(permissions, AGENDA_PERMISSIONS.EDIT_FINANCIAL)

  return (
    <Dialog.Root
      size="xl"
      placement="center"
      open={open}
      onOpenChange={({ open }) => !open && onClose()}
    >
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content
            m={4}
            maxW="900px"
            w="full"
            maxH={{ base: 'calc(100dvh - 96px)', md: '90vh' }}
            display="flex"
            flexDirection="column"
          >
            <Dialog.Header>
              <Dialog.Title>
                {isLoading ? 'Carregando...' : (event?.name ?? 'Detalhes do Evento')}
              </Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <IconButton variant="ghost" size="sm" aria-label="Fechar">
                  <LuX />
                </IconButton>
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body overflowY="auto" flex="1" pb={6}>
              {isLoading ? (
                <Flex justify="center" py={8}>
                  <Spinner size="lg" color="primary.500" />
                </Flex>
              ) : !event ? (
                <Text color="fg.muted">Evento não encontrado.</Text>
              ) : (
                <Flex direction="column" gap={4}>
                  <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
                    <GeneralSection event={event} agendaId={agendaId} canEditEvents={canEditEvents} canEditNotes={canEditNotes} />
                    <AddressSection event={event} agendaId={agendaId} eventId={eventId} canManageEvents={canEditEvents} />
                  </Grid>
                  <RemindersSection event={event} agendaId={agendaId} eventId={eventId} canManageEvents={canEditEvents} />

                  {canViewFinancial && (
                    <FinancialSection event={event} agendaId={agendaId} eventId={eventId} canEditFinancial={canEditFinancial} />
                  )}
                </Flex>
              )}
            </Dialog.Body>

            {!isLoading && event && canEditEvents && (
              <Dialog.Footer>
                <DeleteEventButton agendaId={agendaId} eventId={eventId} onSuccess={onClose} />
              </Dialog.Footer>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default EventDetailsModal
